import { useState, useEffect } from 'react';
import Papa from 'papaparse';

/**
 * Custom hook for caching both raw TSV and parsed JavaScript objects in localStorage with TTL
 *
 * This hook extends useDataCache by also caching the parsed PapaParse results,
 * eliminating the need to re-parse TSV data on every component mount.
 *
 * @param {string} cacheKey - Unique key for localStorage
 * @param {string} url - URL to fetch TSV data from
 * @param {Object} parseOptions - PapaParse options (default: { header: true, delimiter: '\t', dynamicTyping: true })
 * @param {number} ttlMs - Time to live in milliseconds (default: 1 hour)
 * @param {Function} postTransform - Optional function to transform each row after parsing (row => transformedRow)
 * @returns {Object} { data: parsedData, loading, error, clearCache }
 */
const useParsedTSVCache = (
  cacheKey,
  url,
  parseOptions = {
    header: true,
    delimiter: '\t',
    skipEmptyLines: true,
    dynamicTyping: true,
  },
  ttlMs = 3600000,
  postTransform = null
) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastModified, setLastModified] = useState(null);

  // Apply post-transform to parsed data if provided
  const applyTransform = (parsedData) => {
    if (!postTransform || !Array.isArray(parsedData)) {
      return parsedData;
    }
    return parsedData.map(postTransform);
  };

  const clearCache = () => {
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(`${cacheKey}_parsed`);
  };

  const getCachedData = () => {
    // Check if running in browser (not during SSR/react-snap)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return { parsed: null, raw: null };
    }

    try {
      // Try to get parsed data first (best case - instant load)
      const cachedParsed = localStorage.getItem(`${cacheKey}_parsed`);
      if (cachedParsed) {
        const { data: parsedData, timestamp } = JSON.parse(cachedParsed);
        const now = Date.now();

        if (now - timestamp < ttlMs) {
          return { parsed: parsedData, raw: null };
        }
      }

      // Fallback to raw data (need to parse)
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const { data: rawData, timestamp } = JSON.parse(cachedRaw);
        const now = Date.now();

        if (now - timestamp < ttlMs) {
          return { parsed: null, raw: rawData };
        }
      }

      // Cache expired or missing
      clearCache();
      return { parsed: null, raw: null };
    } catch (err) {
      // If there's any error parsing cache, clear it
      clearCache();
      return { parsed: null, raw: null };
    }
  };

  const setCachedData = (rawData, parsedData) => {
    // Check if running in browser (not during SSR/react-snap)
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }

    try {
      const timestamp = Date.now();

      // Cache raw TSV
      localStorage.setItem(cacheKey, JSON.stringify({
        data: rawData,
        timestamp,
      }));

      // Cache parsed data
      localStorage.setItem(`${cacheKey}_parsed`, JSON.stringify({
        data: parsedData,
        timestamp,
      }));
    } catch (err) {
      // If localStorage is full or unavailable, just continue without caching
      console.warn('Failed to cache data:', err);
    }
  };

  useEffect(() => {
    const fetchAndParse = async () => {
      // Check cache first
      const cached = getCachedData();

      if (cached.parsed) {
        // Best case: parsed data already cached - instant load!
        setData(cached.parsed);
        setLoading(false);
        return;
      }

      if (cached.raw) {
        // Parse cached raw data
        Papa.parse(cached.raw, {
          ...parseOptions,
          complete: (results) => {
            if (results.errors.length > 0) {
              setError(`Parse error: ${results.errors[0].message}`);
              setLoading(false);
              return;
            }

            // Apply post-transform before caching
            const parsedData = applyTransform(results.data);

            // Cache the transformed result for next time
            setCachedData(cached.raw, parsedData);

            setData(parsedData);
            setLoading(false);
          },
          error: (err) => {
            setError(err.message);
            setLoading(false);
          },
        });
        return;
      }

      // Cache miss - fetch from network
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }

        // Get last modified date from response headers
        const lastModifiedHeader = response.headers.get('Last-Modified');
        if (lastModifiedHeader) {
          setLastModified(new Date(lastModifiedHeader));
        }

        const rawText = await response.text();

        // Parse the TSV
        Papa.parse(rawText, {
          ...parseOptions,
          complete: (results) => {
            if (results.errors.length > 0) {
              setError(`Parse error: ${results.errors[0].message}`);
              setLoading(false);
              return;
            }

            // Apply post-transform before caching
            const parsedData = applyTransform(results.data);

            // Cache both raw and transformed parsed data
            setCachedData(rawText, parsedData);

            setData(parsedData);
            setLoading(false);
          },
          error: (err) => {
            setError(err.message);
            setLoading(false);
          },
        });
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (url && cacheKey) {
      fetchAndParse();
    }
  }, [url, cacheKey, ttlMs]);

  return { data, loading, error, lastModified, clearCache };
};

export default useParsedTSVCache;
